<script setup lang="ts">
import { ref, watch } from 'vue'
import { 
  VueFlow, 
  useVueFlow, 
  Handle, 
  Position, 
  BaseEdge, 
  EdgeLabelRenderer, 
  getBezierPath
} from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import '@vue-flow/controls/dist/style.css'
import { Button } from '@/components/ui/button'
import { Plus, Network, FolderTree, Trash2, RefreshCw } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'save', mermaidCode: string): void
}>()

const nodes = ref<any[]>([])
const edges = ref<any[]>([])
const showCode = ref(false)
const copied = ref(false)
const editableCode = ref('')
const isParsing = ref(false)

// Inicializar lienzo
const initNodes = () => {
  nodes.value = [
    { id: 'root', type: 'treeNode', position: { x: 300, y: 50 }, data: { id: 'root', label: 'Raíz (Root)' } },
    { id: 'child1', type: 'treeNode', position: { x: 150, y: 150 }, data: { id: 'child1', label: 'Hijo 1' } },
    { id: 'child2', type: 'treeNode', position: { x: 450, y: 150 }, data: { id: 'child2', label: 'Hijo 2' } }
  ]
  edges.value = [
    { id: 'e-root-child1', source: 'root', target: 'child1', type: 'custom' },
    { id: 'e-root-child2', source: 'root', target: 'child2', type: 'custom' }
  ]
}

// Inicializar al abrir
watch(() => props.open, (val) => {
  if (!val) {
    // Limpiar estado al cerrar para que no contamine la siguiente apertura
    nodes.value = []
    edges.value = []
    editableCode.value = ''
    return
  }

  if (val) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (val && nodes.value.length === 0) initNodes()
}, { immediate: true })

const { onConnect } = useVueFlow()

const handleConnect = (params: any) => {
  edges.value.push({
    id: `e-${params.source}-${params.target}-${Date.now()}`,
    source: params.source,
    target: params.target,
    type: 'custom'
  })
}

const addTreeNode = () => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  let nextId = `node_${Date.now()}`

  nodes.value.push({
    id: nextId,
    type: 'treeNode',
    position: { x: Math.random() * 200 + 200, y: Math.random() * 200 + 200 },
    data: { id: nextId, label: 'Nuevo Nodo' }
  })
}

const deleteNode = (id: string) => {
  nodes.value = nodes.value.filter(n => n.id !== id)
  edges.value = edges.value.filter(e => e.source !== id && e.target !== id)
}

const deleteEdge = (id: string) => {
  edges.value = edges.value.filter(e => e.id !== id)
}

const generateCodeString = () => {
  let mermaid = `\n\`\`\`mermaid\n`
  
  const positions: Record<string, any> = {}
  nodes.value.forEach(node => {
    // Para tree view usamos label como key porque mermaid pierde los IDs
    positions[node.data.label] = { x: Math.round(node.position.x), y: Math.round(node.position.y) }
  })
  mermaid += `%% {"positions": ${JSON.stringify(positions)}}\n`
  
  mermaid += `treeView-beta\n`
  
  const incomingCount = new Map<string, number>()
  const childrenMap = new Map<string, string[]>()
  
  nodes.value.forEach(n => {
    incomingCount.set(n.id, 0)
    childrenMap.set(n.id, [])
  })
  
  edges.value.forEach(e => {
    if (incomingCount.has(e.target)) {
      incomingCount.set(e.target, incomingCount.get(e.target)! + 1)
    }
    if (childrenMap.has(e.source)) {
      childrenMap.get(e.source)!.push(e.target)
    }
  })
  
  const roots = nodes.value.filter(n => incomingCount.get(n.id) === 0)
  const visited = new Set<string>()
  
  const printNode = (nodeId: string, depth: number) => {
    if (visited.has(nodeId)) return // Evitar ciclos
    visited.add(nodeId)
    
    const node = nodes.value.find(n => n.id === nodeId)
    if (!node) return
    
    const indent = '  '.repeat(depth + 1)
    const label = node.data.label.replace(/"/g, "'").trim()
    
    // Mermaid treeView-beta syntax requires double quotes around node text
    mermaid += `${indent}"${label}"\n`
    
    const children = childrenMap.get(nodeId) || []
    children.forEach(childId => {
      printNode(childId, depth + 1)
    })
  }
  
  roots.forEach(root => {
    printNode(root.id, 0)
  })
  
  // Nodos desconectados
  nodes.value.forEach(n => {
    if (!visited.has(n.id)) {
      printNode(n.id, 0)
    }
  })

  mermaid += '```\n'
  return mermaid
}

// Escuchar cambios en el UI visual para actualizar el código texto
watch([nodes, edges], () => {
  if (isParsing.value) return
  editableCode.value = generateCodeString()
}, { deep: true, immediate: true })

// Parsear Mermaid a VueFlow
const syncToVisual = () => {
  isParsing.value = true
  try {
    const lines = editableCode.value.split('\n')
    const parsedNodes = new Map<string, any>()
    const parsedEdges: any[] = []
    
    // Simple indentation-based parser
    let currentPath: { id: string, depth: number }[] = []
    let idCounter = 1
    let savedPositions: Record<string, any> | null = null
    
    lines.forEach(line => {
      const originalLine = line
      line = line.replace(/\t/g, '  ')
      
      // Parsear JSON oculto de posiciones
      if (line.trim().startsWith('%% {"positions":')) {
        try {
          savedPositions = JSON.parse(line.trim().replace(/^%% /, '')).positions
        } catch (e) {
          console.error('Error parseando metadata de posiciones', e)
        }
        return
      }
      
      if (!line.trim() || line.startsWith('```') || line.startsWith('%%') || line.trim() === 'treeView-beta' || line.trim() === 'tree') return
      
      const depthMatches = line.match(/^(\s*)/)
      const depth = depthMatches ? depthMatches[1].length : 0
      const label = line.trim().replace(/^"|"$/g, '')
      
      const id = `node_sync_${idCounter++}`
      parsedNodes.set(id, { id, label })
      
      // Encontrar padre basado en profundidad
      while (currentPath.length > 0 && currentPath[currentPath.length - 1].depth >= depth) {
        currentPath.pop()
      }
      
      if (currentPath.length > 0) {
        const parentId = currentPath[currentPath.length - 1].id
        parsedEdges.push({ source: parentId, target: id })
      }
      
      currentPath.push({ id, depth })
    })

    // Reconstruir visuales
    const newNodes: any[] = []
    let currentX = 100, currentY = 100
    
    parsedNodes.forEach((data, id) => {
      let pos = { x: currentX, y: currentY }
      
      if (savedPositions && savedPositions[data.label]) {
        pos = savedPositions[data.label]
      } else {
        const existing = nodes.value.find(n => n.data.label === data.label)
        if (existing) {
          pos = existing.position
        } else {
          currentX += 80; currentY += 80
        }
      }
      
      newNodes.push({
        id, type: 'treeNode', position: pos,
        data: { id, label: data.label }
      })
    })

    const newEdges = parsedEdges.map((e, i) => ({
      id: `e-${e.source}-${e.target}-${i}`,
      source: e.source,
      target: e.target,
      type: 'custom'
    }))

    nodes.value = newNodes
    edges.value = newEdges
  } catch (e) {
    console.error('Error parseando Mermaid Tree:', e)
  }
  
  setTimeout(() => isParsing.value = false, 100)
}

let debounceTimer: ReturnType<typeof setTimeout>
const onCodeChange = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    syncToVisual()
  }, 400)
}

const generateMermaid = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
  initNodes()
}

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch (err) {
    console.error('Failed to copy text', err)
  }
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Diagrama de Árbol (Tree View)"
    description="Construye estructuras jerárquicas como árboles de directorios o estructuras organizacionales. Une los nodos desde la parte inferior hacia la superior para crear ramas."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="generateMermaid"
  >
    <template #icon>
      <FolderTree class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <div class="flex flex-wrap gap-1">
        <Button size="sm" @click="addTreeNode" class="h-7 text-xs"><Plus class="w-3 h-3 mr-1" /> Añadir Nodo</Button>
      </div>
    </template>

    <template #canvas>
      <VueFlow 
        v-model:nodes="nodes" 
        v-model:edges="edges"
        @connect="handleConnect"
        :default-zoom="1"
      >
        <Background />
        <Controls />

        <!-- Node Template -->
        <template #node-treeNode="nodeProps">
          <div class="relative group p-1 flex flex-col items-center justify-center font-sans text-sm">
            <div class="bg-card border-2 border-primary shadow-sm rounded-md flex items-center justify-center p-2 min-w-[120px] transition-all">
              <input 
                v-model="nodeProps.data.label" 
                class="bg-transparent text-center font-medium focus:outline-none focus:ring-1 focus:ring-primary/50 w-full" 
                placeholder="Texto del Nodo" 
              />
            </div>
            
            <Handle type="target" :position="Position.Top" id="top" />
            <Handle type="source" :position="Position.Bottom" id="bottom" />

            <!-- Botón Borrar global -->
            <div class="absolute -top-3 -right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
              <button @click.stop="deleteNode(nodeProps.id)" class="bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center hover:scale-110 shadow-sm" title="Eliminar">×</button>
            </div>
          </div>
        </template>

        <!-- Plantilla para Conexiones -->
        <template #edge-custom="edgeProps">
          <BaseEdge 
            :id="edgeProps.id" 
            :path="getBezierPath(edgeProps)[0]" 
            class="vue-flow__edge-path" 
            :style="{ strokeWidth: 2, stroke: 'hsl(var(--primary))' }"
          />
          <EdgeLabelRenderer>
            <div
              :style="{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${getBezierPath(edgeProps)[1]}px,${getBezierPath(edgeProps)[2]}px)`,
                pointerEvents: 'all',
              }"
              class="nodrag nopan bg-background border shadow-sm rounded p-1 group z-10 flex flex-col items-center justify-center"
            >
              <button 
                @click="deleteEdge(edgeProps.id)" 
                class="text-destructive rounded-full w-5 h-5 flex items-center justify-center text-[12px] opacity-0 group-hover:opacity-100 transition-opacity"
                title="Eliminar Conexión"
              >×</button>
            </div>
          </EdgeLabelRenderer>
        </template>

      </VueFlow>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>

<style scoped>
.vue-flow__handle {
  width: 12px;
  height: 12px;
  background: hsl(var(--primary)) !important;
  border-radius: 50%;
  border: 2px solid hsl(var(--background));
}
.vue-flow__edge-path {
  stroke: hsl(var(--primary));
  stroke-width: 2;
}
</style>
